import React from 'react';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useBranching } from './hooks/useBranching';
import { useQuestionEditor } from './hooks/useQuestionEditor';
import { BranchingOptionRow } from './BranchingOptionRow';
import { navigate } from '../../../common/navigate';
import { PageSpinner } from '../../../common/PageSpinner';
import type { BranchRuleInput } from './types';

const Page = styled('section', {
  padding: '1.5rem',
  maxWidth: '720px',
  display: 'grid',
  gap: '1rem',
});

const PageHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
});

const RulesSection = styled('div', {
  border: '1px solid #c8d8e6',
  borderRadius: '8px',
  overflow: 'hidden',
});

const SectionHeader = styled('div', {
  padding: '0.6rem 0.75rem',
  backgroundColor: '#d9e8f5',
  fontWeight: 600,
  fontSize: '0.875rem',
  color: '#1a3a5c',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '0.75rem',
});

const RowsArea = styled('div', {
  padding: '0.5rem 0.75rem',
});

const ActionsRow = styled('div', {
  display: 'flex',
  gap: '0.75rem',
  marginTop: '0.5rem',
});

const ErrorMsg = styled(Text, {
  margin: 0,
  fontSize: '0.85rem',
  color: '#c0392b',
});

const NoTargetsCallout = styled('div', {
  padding: '0.75rem 1rem',
  backgroundColor: '#fefce8',
  border: '1px solid #fde68a',
  borderRadius: '6px',
  fontSize: '0.875rem',
  color: '#78350f',
  lineHeight: 1.5,
});

function getQuestionIdFromPath(): number {
  if (typeof window === 'undefined') return 0;
  const segments = window.location.pathname.split('/').filter(Boolean);
  const idx = segments.indexOf('questions');
  if (idx === -1) return 0;
  return parseInt(segments[idx + 1] ?? '0', 10);
}

function getSurveyIdFromSearch(): number | undefined {
  if (typeof window === 'undefined') return undefined;
  const v = new URLSearchParams(window.location.search).get('surveyId');
  const n = v ? parseInt(v, 10) : NaN;
  return isNaN(n) ? undefined : n;
}

export const BranchingPage = () => {
  const questionId = React.useMemo(() => {
    return getQuestionIdFromPath();
  }, []);
  const surveyId = React.useMemo(() => {
    return getSurveyIdFromSearch();
  }, []);

  // Redirect if surveyId is missing — branching is always survey-scoped
  React.useEffect(() => {
    if (!surveyId) {
      navigate('/admin/questions');
    }
  }, [surveyId]);

  const { question, isLoading: qLoading } = useQuestionEditor(questionId);
  const { rules, eligibleTargets, isLoading, isSaving, error, saveRules } =
    useBranching(questionId, surveyId ?? 0);

  // Draft state: map { optionId → jumpToQuestionId | null }
  const [draftRules, setDraftRules] = React.useState<
    Map<number, number | null>
  >(new Map());
  const [saveError, setSaveError] = React.useState<string | null>(null);

  // Hydrate draft from loaded rules
  React.useEffect(() => {
    const map = new Map<number, number | null>();
    for (const rule of rules) {
      map.set(rule.questionOptionId, rule.jumpToQuestionId);
    }
    setDraftRules(map);
  }, [rules]);

  const handleRuleChange = (rule: BranchRuleInput | null, optionId: number) => {
    setDraftRules((prev) => {
      const next = new Map(prev);
      if (rule === null) {
        next.set(optionId, null);
      } else {
        next.set(rule.questionOptionId, rule.jumpToQuestionId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaveError(null);
    const toSave: BranchRuleInput[] = [];
    for (const [optionId, jumpId] of draftRules.entries()) {
      if (jumpId !== null) {
        toSave.push({ questionOptionId: optionId, jumpToQuestionId: jumpId });
      }
    }
    try {
      await saveRules(toSave);
    } catch (err) {
      setSaveError((err as Error).message ?? 'Save failed');
    }
  };

  const options = question?.options ?? [];
  const backPath = surveyId
    ? `/admin/questions?surveyId=${surveyId}`
    : '/admin/questions';

  if (!surveyId) return null;

  if (isLoading || qLoading) {
    return (
      <Page>
        <PageSpinner label="Loading branching" />
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <ErrorMsg>Failed to load branching: {error.message}</ErrorMsg>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <Button
          variant="text"
          aria-label="Back to questions"
          onClick={() => {
            return navigate(backPath);
          }}
        >
          <IconSymbol icon="arrow_back" size={18} />
        </Button>
        <Heading level={1} style={{ margin: 0, fontSize: '1.4rem' }}>
          Branching — {question?.questionTitle ?? `Question ${questionId}`}
        </Heading>
      </PageHeader>

      <Text style={{ margin: 0, color: '#555' }}>
        For each option, choose which question the respondent should jump to.
        Leave blank to continue to the next question in order.
      </Text>

      {options.length === 0 ? (
        <Text style={{ color: '#999', fontStyle: 'italic' }}>
          This question has no options configured.
        </Text>
      ) : (
        <React.Fragment>
          {eligibleTargets.length === 0 && (
            <NoTargetsCallout role="note">
              No other questions are assigned to this survey yet. Assign
              additional questions to this survey using{' '}
              <strong>Assign to survey</strong> — they will then appear as jump
              targets in the dropdowns below.
            </NoTargetsCallout>
          )}
          <RulesSection>
            <SectionHeader>
              <span>Option</span>
              <span>Jump to question</span>
            </SectionHeader>
            <RowsArea>
              {options.map((opt) => {
                return (
                  <BranchingOptionRow
                    key={opt.questionOptionId}
                    option={opt}
                    eligibleTargets={eligibleTargets}
                    currentJumpToQuestionId={
                      draftRules.get(opt.questionOptionId) ?? null
                    }
                    onChange={(rule) => {
                      return handleRuleChange(rule, opt.questionOptionId);
                    }}
                  />
                );
              })}
            </RowsArea>
          </RulesSection>
        </React.Fragment>
      )}

      {saveError && <ErrorMsg role="alert">{saveError}</ErrorMsg>}

      <ActionsRow>
        <Button
          onClick={handleSave}
          disabled={isSaving || options.length === 0}
        >
          {isSaving ? 'Saving…' : 'Save branching rules'}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            return navigate(backPath);
          }}
          disabled={isSaving}
        >
          Cancel
        </Button>
      </ActionsRow>
    </Page>
  );
};
